import { useEffect, useState } from "react";
import axios from "axios";
import {
  Activity,
  FileText,
  Layout,
  LogOut,
  Settings,
  Users,
  Grid3X3,
  Home,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useAuth } from "@/hooks/useAuth";

import Logo from "@/assets/img/logoC3S.jpeg";
import { Link, useParams } from "react-router-dom";
import { usePatientContext } from "@/hooks/usePatientContext";
import { capitalize } from "@/utils/capitalize";
import { CaseSize } from "@/components/common/ScreenDimensionForm";

const settingsMenuItems = [
  {
    title: "Paramètres",
    url: "/praticien/profil",
    icon: Settings,
  },
  {
    title: "Mes patients",
    url: "/praticien/patients",
    icon: Users,
  },
];

export const SidebarPraticienV2 = () => {
  const { userContext, logout } = useAuth();
  const { patientInfo } = usePatientContext();
  const { patientId } = useParams();
  const [PatientSidebarGroupVisible, setPatientSidebarGroupVisible] =
    useState(true);
  const [caseSizes, setCaseSizes] = useState<CaseSize[]>([]);
  const [selectedCaseSize, setSelectedCaseSize] = useState<CaseSize | null>(
    null
  );

  const portal = "practitioner"; // Remplacez par le portail approprié

  useEffect(() => {
    // Récupérer les tailles de cases
    axios
      .get(`/${portal}/caseSizes`)
      .then((response) => {
        setCaseSizes(response.data);
        // Vérifier le localStorage pour la taille de case sélectionnée
        const savedCaseSizeId = localStorage.getItem("selectedCaseSizeId");
        const savedCaseSize = response.data.find(
          (cs: CaseSize) => cs.id === Number(savedCaseSizeId)
        );
        if (savedCaseSize) {
          setSelectedCaseSize(savedCaseSize);
        } else if (response.data.length > 0) {
          setSelectedCaseSize(response.data[0]);
        }
      })
      .catch((error) => {
        console.error(
          "Erreur lors de la récupération des tailles de cases:",
          error
        );
        // Gérer l'erreur de manière appropriée
      });
  }, [portal]);

  useEffect(() => {
    if (selectedCaseSize) {
      // Stocker la taille de case sélectionnée dans le localStorage
      localStorage.setItem("selectedCaseSizeId", String(selectedCaseSize.id));
      localStorage.setItem(
        "selectedCaseSizePx",
        String(selectedCaseSize.caseSizePx)
      );
      localStorage.setItem("selectedCaseSizeName", selectedCaseSize.name);
    }
  }, [selectedCaseSize]);

  const patientMenuItems = [
    {
      title: "Tableau de bord",
      url: `/praticien/${patientId}/dossier`,
      icon: Home,
    },
    {
      title: "Fiche patient",
      url: `/praticien/${patientId}/fiche`,
      icon: FileText,
    },
    {
      title: "Résultats",
      url: `/praticien/${patientId}/resultats`,
      icon: Activity,
    },
    {
      title: "Nouvelle séance",
      url: `/praticien/${patientId}/programmation`,
      icon: Layout,
    },
    {
      title: "Champ Visuel",
      url: `/praticien/${patientId}/parametragecv`,
      icon: Grid3X3,
    },
  ];

  let firstName = "undefined";
  if (patientInfo?.firstName) {
    firstName = capitalize(patientInfo.firstName);
  }

  const PatientSidebarGroupItem = () => {
    return (
      <SidebarGroup>
        <SidebarGroupLabel>
          {firstName} {patientInfo?.lastName.toUpperCase()}
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {patientMenuItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild>
                  <Link to={item.url}>
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  useEffect(() => {
    if (
      patientInfo &&
      patientInfo.firstName &&
      patientInfo.lastName &&
      patientInfo?.patientId === Number(patientId)
    ) {
      setPatientSidebarGroupVisible(true);
    } else {
      setPatientSidebarGroupVisible(false);
    }
  }, [patientInfo, patientId]);

  return (
    <Sidebar collapsible="none" className="h-full">
      <SidebarHeader className="border-b p-4">
        <div className="flex items-center space-x-2">
          <div className="h-8 w-8 rounded-lg bg-primary">
            <img
              src={Logo}
              alt="Dashboard Icon"
              className="logo size-full rounded-md"
            />
          </div>
          <div className="w-full">
            <h2 className="text-lg font-semibold text-white">
              IRON
            </h2>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <p className="text-md text-sidebar-foreground/70">
          Portail Neuropsychologue
        </p>
        <p className="mt-5 text-sm text-sidebar-foreground/70">
          {userContext?.clinic}
        </p>

        {caseSizes.length > 0 && (
          <div className="mt-4 w-full">
            <label htmlFor="caseSizeSelect" className="text-s font-large text-sidebar-foreground/70 text-left">
              Taille de l'écran :
            </label>
            <div className="w-full flex justify-center">
              <Select
                value={selectedCaseSize?.id ? String(selectedCaseSize.id) : ""}
                onValueChange={(value) => {
                  const selectedId = Number(value);
                  const cs = caseSizes.find((cs) => cs.id === selectedId);
                  setSelectedCaseSize(cs || null);
                }}
              >
                <SelectTrigger className="w-3/4 mt-1 text-black">
                  <SelectValue placeholder="Choisissez une taille" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Taille de l'écran</SelectLabel>
                    {caseSizes.map((cs) => (
                      <SelectItem key={cs.id} value={String(cs.id)}>
                        {cs.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <SidebarGroup>
          <SidebarGroupLabel>Menu principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {settingsMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {PatientSidebarGroupVisible && <PatientSidebarGroupItem />}
      </SidebarContent>
      <SidebarFooter className="border-t p-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <button className="w-full" onClick={() => logout()}>
                <LogOut className="h-4 w-4" />
                <span>Se déconnecter</span>
              </button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
